import { useEffect, useRef } from "react";

interface UsePollingOptions {
  /** Stop polling after this many consecutive failures. Default: no limit. */
  maxConsecutiveErrors?: number;
  /** While false, polling is suspended — no call is scheduled. Flipping it back to true restarts
   *  from an immediate call. Use to stop once a terminal condition is reached (e.g. backend warm).
   *  Default: true. */
  enabled?: boolean;
  /** When false, the first call waits one interval instead of firing immediately — for data
   *  already fetched on mount by something else. Default: true. */
  leading?: boolean;
}

/**
 * Calls `fn` immediately on mount, then reschedules the next call only after `fn` resolves
 * (recursive setTimeout, not setInterval). This prevents overlapping concurrent requests when
 * `fn` takes longer than `intervalMs` (e.g. a cold-start edge function).
 *
 * Stops scheduling once the component unmounts, after `maxConsecutiveErrors` consecutive
 * failures, or while `enabled` is false. `fn` is stored in a ref — callers do not need to
 * memoize it.
 */
export function usePolling(
  fn: () => Promise<void>,
  intervalMs: number,
  options: UsePollingOptions = {}
): void {
  const { maxConsecutiveErrors, enabled = true, leading = true } = options;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let consecutiveErrors = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    async function fire() {
      try {
        await fnRef.current();
        consecutiveErrors = 0;
      } catch {
        consecutiveErrors++;
        if (maxConsecutiveErrors !== undefined && consecutiveErrors >= maxConsecutiveErrors) return;
      }
      if (!cancelled) {
        timeoutId = setTimeout(fire, intervalMs);
      }
    }

    if (leading) void fire();
    else timeoutId = setTimeout(fire, intervalMs);

    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [intervalMs, maxConsecutiveErrors, enabled, leading]);
}
